import { Box, Chip, ChipProps, Typography, Stack } from '@mui/material'
import { FeatureFlagIdExpiringDelegatedUsers } from 'c1gen/features'
import { UserV1UserServiceSetExpiringUserDelegationBindingSelf } from 'c1gen/permissions'
import { C1Chip } from 'c1ui/components/C1Chip'
import { C1Card } from 'components/core/card/C1Card'
import { C1EmptyIndicator } from 'components/core/empty-indicator/C1EmptyIndicator'
import { PageHeader } from 'components/core/page-header'
import { SpacingVariants } from 'components/core/table-grid'
import { C1TableGrid } from 'components/core/table-grid/C1TableGrid'
import { UserDirectoriesProps, UserDirectories, UserDirectoryStatus, UserEmployeeId, UserJob } from 'components/core/user'
import { VariableText } from 'components/core/variable-text'
import { HasCapability, useFeatureChecker } from 'components/no-ui/capability'
import { removeDoubleQuotes } from 'components/no-ui/string-formatting'
import { DelegateUserDetails } from 'components/pages/profile/delegate-user-details'
import { DirectReportsForUser } from 'components/pages/profile/direct-reports-for-user'
import { ExpiringDelegatedUserDetails } from 'components/pages/profile/expiring-delegates/expiring-delegate-user-details'
import { ProfileRow } from 'components/pages/profile/profile-row'
import { UserProfileTypes } from 'components/pages/profile/user-profile-types'
import { userStatusToIdentifier, userStatusToIntl } from 'components/pages/user/type'
import { UserDisplayName } from 'components/pages/user/user-display-name'
import { UserValue } from 'lib/actions/user'
import { UserContext } from 'lib/context/app'
import { Value } from 'pbts/google/protobuf/struct'
import { FC, useContext } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

export const UserEmploymentStatus: FC<UserDirectoriesProps> = ({ user, intl }) => {
    if (!user) {
        return null
    }

    const employmentStatus = user.employmentStatus || 'Unknown'
    const isUserEnabled = employmentStatus.toUpperCase() === 'ACTIVE'

    return (
        <Box display="flex" width="100%">

            <Chip color={isUserEnabled ? 'success' : 'info'} label={isUserEnabled ? intl.formatMessage({ id: 'Components_UserView_Status_Enabled', defaultMessage: 'Active' }) : employmentStatus} />
        </Box>
    )
}

const UserLocation: FC<UserDirectoriesProps> = ({ user }) => {
    if (!user?.profile) {
        return null
    }

    // This code is a bit of a hack to get the city and state from the profile details which may or may not be present.
    // It's not ideal, but it's the best we can do for now.
    const extractStringValue = (value: Value | undefined): string => {
        if (!value) return ''
        return removeDoubleQuotes(Value.toJsonString(value, { prettySpaces: 2 }))
    }

    const city = extractStringValue(user.profile.fields?.city)
    const state = extractStringValue(user.profile.fields?.state)
    const location = [city, state].filter(Boolean).join(', ')

    if (!location) {
        return null
    }

    return (
        <Typography noWrap textAlign="center" color="text.secondary">
            {location}
        </Typography>
    )
}

export const MyProfile: FC = () => {
    const intl = useIntl()
    const { user: userContext }: {
        user: UserValue
    } = useContext(UserContext)

    const userName = userContext?.displayName || ''
    const extraEmails = userContext?.emails?.filter((e) => e !== userContext?.email) ?? []
    const extraUsernames = userContext?.usernames?.filter((e) => e !== userContext?.username) ?? []
    const identifierProps = userStatusToIdentifier(userContext?.status, intl)
    const hasExpiringDelegatedUsersFF = useFeatureChecker(FeatureFlagIdExpiringDelegatedUsers)

    const userStatusChip: ChipProps = {
        color: identifierProps.color,
        label: userContext ? userStatusToIntl(userContext.status, intl) : '',
    }

    return (
        <>
            <Box my={4}>
                <PageHeader
                    title={
                        <Typography variant="h2">
                            <FormattedMessage id="profile_title" defaultMessage="My profile" />
                        </Typography>
                    }
                />
            </Box>
            <Box
                display="grid"
                gridTemplateColumns={{ xs: '1fr', md: '30% 1fr' }}
                gap={5}
            >
                <Box>
                    <C1Card
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 4,
                            px: 4,
                            py: 5,
                        }}
                    >
                        <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                            <UserDisplayName
                                user={userContext}
                                avatarSize="xl"
                                readOnly
                                iconOnly
                                avatarIsEditable
                            />
                        </Box>

                        <Box display="flex" flexDirection="column" alignItems="center">
                            <Typography noWrap textAlign="center" variant="h2" mb={1}>{userName}</Typography>
                            <Box display="flex" alignItems="center">
                                <Typography noWrap>{userContext?.email}</Typography>
                            </Box>
                            {extraEmails.map((e) => {
                                return <div key={e}><Typography noWrap>{e}</Typography></div>
                            })}
                        </Box>

                        <UserLocation user={userContext} intl={intl} />

                        {userStatusChip.label && <Chip color={userStatusChip.color} label={userStatusChip.label} />}

                        <UserProfileTypes userId={userContext?.id || ''} />
                    </C1Card>
                </Box>

                <Stack flex="1" spacing={5}>
                    <C1TableGrid
                        title={<FormattedMessage id="user_details_header" defaultMessage="User details" />}
                        spacingVariant={SpacingVariants.compact}
                    >
                        <Box display="flex" flexDirection="row" flexWrap="wrap" gap={3} px={4}>
                            <ProfileRow label={intl.formatMessage({
                                id: 'user_details_username',
                                defaultMessage: 'Username',
                            })}
                            >
                                <Box>
                                    <Box display="flex" alignItems="center">
                                        <VariableText
                                            width="100%"
                                            noWrap
                                            variableText={userContext?.username}
                                        />
                                        {extraUsernames.length > 0 &&
                                            <C1Chip
                                                label={intl.formatMessage({
                                                    id: 'user_details_primary_username',
                                                    defaultMessage: 'Primary',
                                                })}
                                                sx={{ marginLeft: 1 }}
                                            />
                                        }
                                    </Box>
                                    {extraUsernames?.map((e) => {
                                        return <Box key={e}><Typography>{e}</Typography></Box>
                                    })}
                                </Box>

                            </ProfileRow>

                            <ProfileRow label={intl.formatMessage({
                                id: 'user_details_managers',
                                defaultMessage: 'Managers',
                            })}
                            >
                                <VariableText
                                    component="div"
                                    variableText={userContext?.managers?.map((manager) => (
                                        <UserDisplayName
                                            key={manager.id}
                                            user={manager}
                                            includeDetails={false}
                                            readOnly
                                        />
                                    ))}
                                    emptyState={<C1EmptyIndicator />}
                                />
                            </ProfileRow>
                            <ProfileRow label={intl.formatMessage({
                                id: 'user_details_direct_reports',
                                defaultMessage: 'Direct reports',
                            })}
                            >
                                <DirectReportsForUser managerId={userContext.id} />
                            </ProfileRow>
                            <ProfileRow label={intl.formatMessage({
                                id: 'user_details_job_title',
                                defaultMessage: 'Job title',
                            })}
                            >
                                <UserJob user={userContext} intl={intl} />
                            </ProfileRow>
                            <ProfileRow label={intl.formatMessage({
                                id: 'user_details_department',
                                defaultMessage: 'Department',
                            })}
                            >
                                <VariableText
                                    width="100%"
                                    noWrap
                                    variableText={userContext?.department}
                                    emptyState={<C1EmptyIndicator />}
                                />
                            </ProfileRow>
                            <ProfileRow label={intl.formatMessage({
                                id: 'user_details_employee_id',
                                defaultMessage: 'Employee ID',
                            })}
                            >
                                <UserEmployeeId user={userContext} intl={intl} />
                            </ProfileRow>
                            <ProfileRow label={intl.formatMessage({
                                id: 'user_details_directories',
                                defaultMessage: 'Directory',
                            })}
                            >
                                <UserDirectories user={userContext} intl={intl} readOnly />
                            </ProfileRow>
                            <ProfileRow label={intl.formatMessage({
                                id: 'user_details_directory_status',
                                defaultMessage: 'Directory status',
                            })}
                            >
                                <UserDirectoryStatus user={userContext} intl={intl} />
                            </ProfileRow>
                            <ProfileRow label={intl.formatMessage({
                                id: 'user_details_employment_status',
                                defaultMessage: 'Employment status',
                            })}
                            >
                                <UserEmploymentStatus user={userContext} intl={intl} />
                            </ProfileRow>
                            <ProfileRow label={intl.formatMessage({
                                id: 'user_details_employment_type',
                                defaultMessage: 'Employment type',
                            })}
                            >
                                <VariableText
                                    width="100%"
                                    noWrap
                                    variableText={userContext?.employmentType}
                                    emptyState={<C1EmptyIndicator />}
                                />
                            </ProfileRow>

                        </Box>
                    </C1TableGrid>
                    {userContext ? (
                        hasExpiringDelegatedUsersFF ? (
                            <HasCapability permission={UserV1UserServiceSetExpiringUserDelegationBindingSelf}>
                                <ExpiringDelegatedUserDetails userId={userContext.id} />
                            </HasCapability>
                        ) : (
                            <DelegateUserDetails delegatedUser={userContext.delegatedUser} userId={userContext.id} />
                        )
                    ) : null}
                </Stack>
            </Box>
        </>
    )
}
